import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import axios from "axios";
import StaffLayout from "./staff/StaffLayout";
import { getSessionDisplayName } from "../utils/sessionTypeUtils";
import { hasAnyAddon } from "../utils/addonIndicatorUtils";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./SuiteCalendar.css";

const localizer = momentLocalizer(moment);

// Helper function to get operating hours (same as your backend)
const getOperatingHours = (m) => {
  const day = m.day();
  if (day === 0 || day === 6) {
    return { startHour: 7, endHour: 20 };
  } else {
    return { startHour: 7, endHour: 21 };
  }
};

function SuiteCalendar({
  suiteNumber,
  suiteType,
  selectedDate,
  hideHeader,
  hideToolbar,
  hideGutter,
}) {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(
    selectedDate ? new Date(selectedDate) : new Date()
  );
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [note, setNote] = useState("");
  const [customerNote, setCustomerNote] = useState("");
  const token = localStorage.getItem("jwtToken");

  useEffect(() => {
    async function fetchBookings() {
      try {
        const endpoint =
          suiteType === "sauna"
            ? `${process.env.REACT_APP_API_BASE_URL}/suites/sauna/${suiteNumber}/bookings`
            : suiteType === "massage"
            ? `${process.env.REACT_APP_API_BASE_URL}/suites/massage/${suiteNumber}/bookings`
            : `${process.env.REACT_APP_API_BASE_URL}/suites/redlight/${suiteNumber}/bookings`;
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const events = response.data.bookings.map((booking) => {
          // STAFF-ADDON-VISIBILITY-001 — compact add-on marker.
          // Graceful degradation: BE projection may not include these
          // fields yet (Simone's PR firing in parallel); hasAnyAddon
          // returns false in that case so the marker simply does not
          // render until BE deploys.
          const addonMarker = hasAnyAddon(booking) ? "* " : "";
          return {
            title: `${addonMarker}${booking.fullName || "Unknown User"} • ${getSessionDisplayName(
              booking.sessionType
            )}`,
            start: new Date(booking.appointmentDate),
            end: new Date(
              new Date(booking.appointmentDate).getTime() +
                booking.duration * 60000
            ),
            allDay: false,
            bookingId: booking._id,
            status: booking.status,
            hasAddon: hasAnyAddon(booking),
          };
        });
        setEvents(events);
      } catch (err) {
        console.error("Error fetching suite bookings:", err);
      }
    }
    fetchBookings();
  }, [suiteNumber, suiteType, token, selectedDate]);

  // Get operating hours for the current date
  const { startHour, endHour } = getOperatingHours(moment(currentDate));
  const minTime = moment(currentDate).startOf("day").hour(startHour).toDate();
  const maxTime = moment(currentDate).startOf("day").hour(endHour).toDate();

  // Update currentDate when navigation occurs
  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  // When an event is clicked, fetch and display its details in a modal.
  const handleSelectEvent = async (event) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/staff/bookings/${event.bookingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedBooking(response.data.booking);
      setCustomerNote(response.data.booking.user.customerNote || "");
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching booking details:", error);
    }
  };

  // Save customer note to the user record.
  const handleSaveCustomerNote = async () => {
    try {
      /*console.log("DEBUG::Saving customer note:", customerNote);
      console.log("DEBUG::Updating user with id:", selectedBooking.user._id);*/
      // Update the associated user with the customer note.
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/staff/users/${selectedBooking.user._id}`,
        { customerNote }, // sending the note in the request body
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Optionally update selectedBooking with the updated user details.
      setSelectedBooking({
        ...selectedBooking,
        user: response.data.updatedUser,
      });
      alert("Customer note saved successfully!");
      setShowModal(false);
    } catch (error) {
      console.error("Error saving customer note:", error);
      alert("Error saving customer note.");
    }
  };

  return (
    <StaffLayout>
      <div
        className={`suite-calendar ${hideHeader ? "hide-header" : ""} ${
          hideGutter ? "hide-gutter" : ""
        }`}
      >
        {!hideHeader && (
          <h3>
            {suiteType === "sauna"
              ? `Sauna Suite #${suiteNumber}`
              : suiteType === "massage"
              ? `Massage Suite #${suiteNumber}`
              : `Red Light Suite #${suiteNumber}`}
          </h3>
        )}
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          defaultView="day"
          date={currentDate}
          onNavigate={handleNavigate}
          onSelectEvent={handleSelectEvent}
          min={minTime}
          max={maxTime}
          style={{ height: 400 }}
          components={{
            toolbar: hideToolbar ? () => null : undefined,
          }}
        />

        {/* Modal for displaying session & customer details and capturing notes */}
        {showModal && selectedBooking && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Session Details</h3>
              <p>
                <strong>Customer:</strong> {selectedBooking.fullName}
              </p>
              {selectedBooking.user && selectedBooking.user.profilePicture && (
                <img
                  src={selectedBooking.user.profilePicture}
                  alt={`${selectedBooking.fullName}'s profile`}
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                  }}
                />
              )}
              <p>
                <strong>Appointment Date:</strong>{" "}
                {new Date(selectedBooking.appointmentDate).toLocaleString()}
              </p>
              <p>
                <strong>Service:</strong>{" "}
                {getSessionDisplayName(selectedBooking.sessionType)}
              </p>
              <p>
                <strong>Status:</strong> {selectedBooking.status}
              </p>
              {/* Additional session details can be added here */}
              <div style={{ marginTop: "10px" }}>
                <label>
                  <strong>Notes:</strong>
                </label>
                <textarea
                  value={customerNote}
                  onChange={(e) => {
                    //console.log("DEBUG::New customer note:", e.target.value);
                    setCustomerNote(e.target.value);
                  }}
                  rows="4"
                  style={{ width: "100%" }}
                />
              </div>
              <div style={{ marginTop: "10px", textAlign: "right" }}>
                <button onClick={() => setShowModal(false)}>Cancel</button>
                <button
                  onClick={handleSaveCustomerNote}
                  style={{ marginLeft: "10px" }}
                >
                  Save Note
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </StaffLayout>
  );
}

export default SuiteCalendar;
